import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import { Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import { PhWarningCircle, PhQuotes, PhArrowRight } from 'ember-phosphor-icons';
import AlertBanner from 'tio-reimagined/components/alert-banner';
import type TasApplicationModel from 'tio-common/models/tas-application';
import type { TransitionLogSignature } from 'tio-common/types/tuition-assistance';

interface RejectionDetailSignature {
  Args: {
    application: TasApplicationModel;
    /** Called when user clicks the fix/resubmit CTA */
    onFix?: () => void;
  };
  Element: HTMLDivElement;
}

const COURSE_REJECTION_STATES = [
  'TAS.ApplicationState.COURSES_NOT_APPROVED',
  'TAS.ApplicationState.COURSES_REJECTED',
];
const EVIDENCE_REJECTION_STATES = [
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
  'TAS.ApplicationState.EVIDENCE_REJECTED',
];

/**
 * RejectionDetail - Displays rejection reasons with actionable fix guidance
 *
 * Reads the most recent rejection event from the application's transitionLog
 * and shows who rejected, when, their note, and what the user should do next.
 *
 * @example
 * ```gts
 * <RejectionDetail
 *   @application={{this.application}}
 *   @onFix={{this.navigateToWizard}}
 * />
 * ```
 */
export default class RejectionDetail extends Component<RejectionDetailSignature> {
  @service declare intl: IntlService;

  get application() {
    return this.args.application;
  }

  get isCoursesRejection() {
    return COURSE_REJECTION_STATES.includes(this.application.state);
  }

  get isEvidenceRejection() {
    return EVIDENCE_REJECTION_STATES.includes(this.application.state);
  }

  get isRejected() {
    return this.isCoursesRejection || this.isEvidenceRejection;
  }

  get rejectionLog(): TransitionLogSignature | undefined {
    const logs = this.application.transitionLog || [];
    // Find the most recent rejection event (search from end)
    const rejectionEvents = [
      'reject_courses',
      'not_approve_courses',
      'reject_evidence',
      'not_approve_evidence',
    ];

    for (let i = logs.length - 1; i >= 0; i--) {
      const log = logs[i];
      if (log && rejectionEvents.includes(log.event)) {
        return log;
      }
    }
    return undefined;
  }

  get rejectedBy() {
    return this.rejectionLog?.reason?.by?.name;
  }

  get rejectedDate() {
    if (!this.rejectionLog?.timestamp) return '';
    const date = new Date(this.rejectionLog.timestamp);
    return this.intl.formatDate(date, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  get reviewerNote() {
    return this.rejectionLog?.reason?.note;
  }

  get titleKey() {
    return this.isCoursesRejection
      ? 'tuition_assistance.reimagined.rejection.courses_rejected_title'
      : 'tuition_assistance.reimagined.rejection.evidence_rejected_title';
  }

  get guidanceKey() {
    return this.isCoursesRejection
      ? 'tuition_assistance.reimagined.rejection.courses_guidance'
      : 'tuition_assistance.reimagined.rejection.evidence_guidance';
  }

  get ctaKey() {
    return this.isCoursesRejection
      ? 'tuition_assistance.reimagined.rejection.cta_fix_courses'
      : 'tuition_assistance.reimagined.rejection.cta_fix_evidence';
  }

  <template>
    {{#if this.isRejected}}
      <div
        class="rounded-2xl border-2 border-amber-200 bg-amber-50/50 p-6 dark:border-amber-800 dark:bg-amber-950/30"
        ...attributes
      >
        <div class="flex items-start gap-3 mb-4">
          <PhWarningCircle
            @size={{24}}
            @weight="fill"
            class="shrink-0 text-amber-600 dark:text-amber-400 mt-0.5"
          />
          <div>
            <div class="font-semibold text-amber-800 dark:text-amber-200">
              {{t this.titleKey}}
            </div>
            {{#if this.rejectedBy}}
              <Text class="text-sm text-amber-700 dark:text-amber-300 mt-1">
                {{t
                  "tuition_assistance.reimagined.rejection.rejected_by"
                  name=this.rejectedBy
                  date=this.rejectedDate
                }}
              </Text>
            {{/if}}
          </div>
        </div>

        {{! Reviewer note }}
        <div class="mb-4 rounded-xl bg-white/80 p-4 dark:bg-gray-800/50">
          <div class="flex items-start gap-2">
            <PhQuotes @size={{16}} class="shrink-0 text-amber-500 dark:text-amber-400 mt-0.5" />
            <div>
              <div class="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                {{t "tuition_assistance.reimagined.rejection.reviewer_note"}}
              </div>
              <Text class="text-sm text-gray-800">
                {{#if this.reviewerNote}}
                  {{this.reviewerNote}}
                {{else}}
                  {{t "tuition_assistance.reimagined.rejection.no_note"}}
                {{/if}}
              </Text>
            </div>
          </div>
        </div>

        {{! Guidance }}
        <AlertBanner @intent="tip" class="mb-4">
          {{t this.guidanceKey}}
        </AlertBanner>

        {{! CTA }}
        {{#if @onFix}}
          <Button @intent="primary" {{on "click" @onFix}}>
            <PhArrowRight @size={{16}} class="mr-1" />
            {{t this.ctaKey}}
          </Button>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
